import type {
  ActivityType,
  Civility,
  ContactStatus,
  KycStatus,
  OppSource,
  Stage,
  WealthSource,
  WealthTranche,
} from '@crm/shared';

export type Activity = {
  id: string;
  type: ActivityType;
  activity_date: string;
  duration_minutes: number | null;
  counterparty: string | null;
  summary: string | null;
  creator: { id: string; display_name: string } | null;
};

export type StatusHistoryEntry = {
  id: string;
  from_stage: Stage | null;
  to_stage: Stage;
  changed_at: string;
  override_probability: number | null;
  override_reason: string | null;
  user: { id: string; display_name: string } | null;
};

export type PortfolioSummary = {
  id: string;
  custodian: { id: string; code: string; name: string };
  aum_chf: number;
  asset_allocation: Record<string, number>;
  performance_ytd_pct: number | null;
  last_imported_at: string;
};

export type OpportunityFull = Opportunity & {
  expected_timeframe: string | null;
  referred_by: string | null;
  stage_probability_override_reason: string | null;
  activities: Activity[];
  status_history: StatusHistoryEntry[];
  client: {
    id: string;
    onboarded_at: string;
    portfolios: PortfolioSummary[];
  } | null;
};

export type DashboardData = {
  pipeline_by_stage: Array<{
    stage: Stage;
    count: number;
    total_initial_amount_chf: number;
    total_score_chf: number;
  }>;
  top_opportunities: Array<{
    id: string;
    name: string;
    stage: Stage;
    score_chf: number;
    initial_expected_amount: number;
    roa_bps: number;
    target_bank_code: string | null;
  }>;
  aum_chf: number;
  nnm_mtd_chf: number;
  total_potential_chf: number;
  revenue_forecast_chf: number;
  targets: {
    nnm_target_chf: number;
    revenue_target_chf: number;
    roa_target_bps: number;
  };
  alerts: Array<{
    id: string;
    kind: 'kyc_expired' | 'compliance_review_stale' | 'eu_reverse_solicitation_missing';
    opportunity_id: string;
    opportunity_name: string;
    details: string;
  }>;
};

export type Opportunity = {
  id: string;
  name: string;
  passport_origin: string | null;
  domiciled_country: string;
  industry_sector: string;
  total_wealth: number;
  initial_expected_amount: number;
  potential_expected_amount_y1: number;
  /** When true, column I shows the text marker "Negociation" instead of a CHF amount. */
  potential_negotiation: boolean;
  /** Nullable — legacy rows imported without a defined RoA leave this blank. */
  roa_bps: number | null;
  /** Computed server-side; null when inputs are missing (Excel formula H). */
  estimated_roa_chf: number | null;
  /** Computed server-side; null when potential is "Negociation" or roa_bps null (formula K). */
  expected_roa_chf: number | null;
  expected_timeframe: string | null;
  stage: Stage;
  stage_probability_pct: number;
  stage_probability_override: number | null;
  score_chf: number;
  kyc_status: 'pending' | 'in_progress' | 'complete' | 'expired';
  kyc_last_review_date: string | null;
  pep_flag: boolean;
  source: string;
  existing_bank: string | null;
  target_bank: { id: string; code: string; name: string } | null;
  owner_rm: { id: string; display_name: string } | null;
  reverse_solicitation_evidence: string | null;
  contact_id: string | null;
  created_at: string;
  updated_at: string;
};

export type Custodian = {
  id: string;
  code: string;
  name: string;
  country: string;
};

export type OpportunityInlineEditPayload = Partial<{
  name: string;
  passport_origin: string | null;
  domiciled_country: string;
  industry_sector: string;
  total_wealth: number;
  initial_expected_amount: number;
  roa_bps: number | null;
  potential_expected_amount_y1: number;
  potential_negotiation: boolean;
  expected_timeframe: string | null;
  existing_bank: string | null;
  target_bank_id: string | null;
  source: OppSource;
  referred_by: string | null;
  pep_flag: boolean;
  kyc_status: 'pending' | 'in_progress' | 'complete' | 'expired';
  kyc_last_review_date: string | null;
  reverse_solicitation_evidence: string | null;
}>;

type ApiOptions = RequestInit & { json?: unknown };

async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { json, headers, ...rest } = opts;
  const body = json !== undefined ? JSON.stringify(json) : (rest.body ?? undefined);
  // Only set Content-Type when there's a body. Fastify rejects an empty
  // body when the header is present (FST_ERR_CTP_EMPTY_JSON_BODY) — that
  // breaks bodyless DELETEs.
  const computedHeaders: Record<string, string> = { ...(headers as Record<string, string>) };
  if (body !== undefined && !('Content-Type' in computedHeaders)) {
    computedHeaders['Content-Type'] = 'application/json';
  }
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'include',
    headers: computedHeaders,
    body,
    ...rest,
  });
  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      // swallow
    }
    const err = new Error(`API ${res.status} ${res.statusText}`) as Error & {
      status: number;
      payload: unknown;
    };
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  // 204 No Content (e.g. DELETE) — return undefined; the caller types T as void.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  me: () =>
    api<{
      id: string;
      email: string;
      display_name: string;
      role: string;
      preferred_locale: string;
    }>('/auth/me'),
  listOpportunities: () => api<Opportunity[]>('/opportunities'),
  listManagerOpportunities: () => api<Opportunity[]>('/manager/opportunities'),
  getOpportunityFull: (id: string) => api<OpportunityFull>(`/opportunities/${id}/full`),
  patchOpportunity: (id: string, body: OpportunityInlineEditPayload) =>
    api<Opportunity>(`/opportunities/${id}`, { method: 'PATCH', json: body }),
  deleteOpportunity: (id: string) => api<void>(`/opportunities/${id}`, { method: 'DELETE' }),
  listCustodians: () => api<Custodian[]>('/custodians'),
  createActivity: (
    opportunityId: string,
    body: {
      type: 'visit' | 'call' | 'email' | 'meeting' | 'note';
      activity_date: string;
      duration_minutes?: number;
      counterparty?: string;
      summary?: string;
    },
  ) =>
    api<Activity>(`/opportunities/${opportunityId}/activities`, {
      method: 'POST',
      json: body,
    }),
  getDashboardRm: () => api<DashboardData>('/dashboard/rm'),
  transitionStage: (
    id: string,
    body: { to_stage: Stage; override_probability?: number; override_reason?: string },
  ) =>
    api<{
      id: string;
      stage: Stage;
      stage_probability_pct: number;
      stage_probability_override: number | null;
      score_chf: number;
    }>(`/opportunities/${id}/stage`, { method: 'POST', json: body }),
  // -------------------------------------------------------------------------
  // Contacts (prospects directory — E2)
  // -------------------------------------------------------------------------
  listContacts: (params: ListContactsParams = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') q.set(k, String(v));
    }
    const qs = q.toString();
    return api<ContactRow[]>(`/contacts${qs ? `?${qs}` : ''}`);
  },
  getContact: (id: string) => api<ContactRow>(`/contacts/${id}`),
  createContact: (body: ContactCreatePayload) =>
    api<ContactRow>('/contacts', { method: 'POST', json: body }),
  updateContact: (id: string, body: ContactCreatePayload) =>
    api<ContactRow>(`/contacts/${id}`, { method: 'PATCH', json: body }),
  deleteContact: (id: string) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),
  scanBusinessCard: (body: { image_base64: string; mime_type: string }) =>
    api<ContactScanResponse>('/contacts/scan', { method: 'POST', json: body }),
  createOpportunityForContact: (body: OpportunityFromContactPayload) =>
    api<Opportunity>('/opportunities', { method: 'POST', json: body }),
};

export type ContactAddressRow = {
  id?: string;
  label: string;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  is_primary: boolean;
};

export type ContactOpportunitySummary = {
  id: string;
  name: string;
  stage: Stage;
  score_chf: number;
  initial_expected_amount: number;
};

export type ContactRow = {
  id: string;
  civility: Civility | null;
  first_name: string;
  last_name: string;
  nickname: string | null;
  photo_url: string | null;
  email_primary: string | null;
  email_secondary: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  linkedin_url: string | null;
  company: string | null;
  position: string | null;
  industry_sector: string | null;
  date_of_birth: string | null;
  nationalities: string[];
  tax_residence: string | null;
  languages: string[];
  wealth_tranche: WealthTranche;
  wealth_source: WealthSource;
  pep_flag: boolean;
  id_document_type: string | null;
  id_document_masked: string | null;
  id_document_expiry: string | null;
  kyc_last_review_date: string | null;
  kyc_status: KycStatus;
  source: OppSource;
  referred_by_contact: string | null;
  first_met_at: string | null;
  owner_rm_id: string | null;
  owner_rm: { id: string; display_name: string } | null;
  tags: string[];
  notes: string | null;
  created_at: string;
  updated_at: string;
  last_interaction_at: string | null;
  status: ContactStatus;
  addresses: ContactAddressRow[];
  opportunities: ContactOpportunitySummary[];
};

export type ListContactsParams = {
  q?: string;
  owner_rm_id?: string;
  wealth_tranche?: WealthTranche | '';
  source?: OppSource | '';
  pep?: 'true' | 'false' | '';
  tag?: string;
  status?: ContactStatus | '';
  limit?: number;
};

export type ContactCreatePayload = Partial<
  Omit<
    ContactRow,
    | 'id'
    | 'created_at'
    | 'updated_at'
    | 'last_interaction_at'
    | 'status'
    | 'opportunities'
    | 'owner_rm'
    | 'addresses'
  >
> & {
  first_name: string;
  last_name: string;
  addresses?: Array<Omit<ContactAddressRow, 'id'>>;
};

/**
 * Response from POST /api/v1/contacts/scan. The fields object mirrors the
 * shared `ContactScanResultSchema` produced by Claude Vision via tool_use.
 * Arrays (emails, phones) preserve visual prominence order on the card.
 * `confidence` drives the badge displayed in the preview screen.
 */
export type ContactScanResponse = {
  model: string;
  image_sha256: string;
  fields: {
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    company: string | null;
    department: string | null;
    emails: string[];
    phones: Array<{
      raw: string;
      type: 'mobile' | 'office' | 'fax' | 'unknown';
      e164: string | null;
    }>;
    website: string | null;
    linkedinUrl: string | null;
    addressLine: string | null;
    postalCode: string | null;
    city: string | null;
    stateOrRegion: string | null;
    country: string | null;
    confidence: {
      overall: 'high' | 'medium' | 'low';
      notes: string;
    };
  };
};

export type OpportunityFromContactPayload = {
  contact_id: string;
  name: string;
  domiciled_country: string;
  industry_sector: string;
  total_wealth: number;
  initial_expected_amount: number;
  potential_expected_amount_y1?: number;
  roa_bps: number;
  expected_timeframe?: string;
  existing_bank?: string;
  target_bank_id?: string;
  source: OppSource;
  referred_by?: string;
  pep_flag?: boolean;
};
